// Standard draggable splitter — a 3px bar that captures the pointer and reports
// each drag move to its parent, which owns the geometry (panel width, split
// fraction, …). Use `orientation="vertical"` for a vertical bar between
// side-by-side panes (col-resize) and `"horizontal"` for a bar between stacked
// panes (row-resize).

import { useRef, type PointerEvent as ReactPointerEvent } from "react";
import { cx } from "../lib/classNames";

export interface ResizeHandleProps {
  readonly orientation: "vertical" | "horizontal";
  /** Called on every pointer move while the handle is being dragged. */
  readonly onResize: (e: ReactPointerEvent) => void;
  readonly ariaLabel?: string;
  readonly className?: string;
}

/** Standard handle thickness (px). */
export const RESIZE_HANDLE_SIZE = 2;

export function ResizeHandle({ orientation, onResize, ariaLabel, className }: ResizeHandleProps) {
  const dragging = useRef(false);
  const isVertical = orientation === "vertical";

  const end = (e: ReactPointerEvent) => {
    dragging.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* no capture — ignore */
    }
  };

  return (
    <div
      role="separator"
      aria-orientation={isVertical ? "vertical" : "horizontal"}
      aria-label={ariaLabel}
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (dragging.current) onResize(e);
      }}
      onPointerUp={end}
      onPointerCancel={end}
      className={cx(
        "shrink-0 bg-slate-800 transition-colors hover:bg-arch-600",
        isVertical ? "w-[2px] cursor-col-resize" : "h-[2px] cursor-row-resize",
        className,
      )}
    />
  );
}
